import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Banknote, ShoppingBag, ArrowUpRight } from "lucide-react";

const DIVISIONS = [
  {
    icon: Banknote,
    tag: "Division 01",
    title: "Capital Funding",
    subtitle: "Strategic capital. Smart structuring. Closed deals.",
    desc: "Tailored debt, equity, project finance and advisory for MSMEs, startups, manufacturers and large enterprises — backed by India's leading banks and NBFCs.",
    bullets: ["Equity & Investor Connects", "Debt & Working Capital", "Project Finance · LAP · IPO"],
    route: "/capital-funding",
    cta: "Enter Capital Funding",
    theme: "dark",
  },
  {
    icon: ShoppingBag,
    tag: "Division 02",
    title: "FMCG Brand House",
    subtitle: "Modern Indian brands, built with intent.",
    desc: "We incubate and operate consumer brands — Chai Yoga, Grain Story and Cookie Magic — designed for the modern Indian shelf and the discerning Indian household.",
    bullets: ["Chai Yoga · Tea & Wellness", "Grain Story · Staples & Grains", "Cookie Magic · Bakery & Treats"],
    route: "/fmcg",
    cta: "Enter FMCG Brand House",
    theme: "light",
  },
];

export default function DivisionsOverview() {
  const navigate = useNavigate();

  return (
    <section
      id="divisions"
      data-testid="divisions-overview-section"
      className="bg-[#FAFAF7] py-24 md:py-32 border-t border-[#0A1F44]/8"
    >
      <div className="cgp-container">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <span className="cgp-overline">Our Two Divisions</span>
          <h2 className="font-serif font-light text-[#0A1F44] mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            One firm.
            <br />
            <span className="italic text-[#0A1F44]/70">Two specialised arms.</span>
          </h2>
          <p className="mt-7 text-[#0B1A36]/70 text-lg leading-relaxed max-w-2xl">
            Step into either division for a focused look at how Catalyst
            partners with businesses and brands across India.
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {DIVISIONS.map((d, i) => (
            <motion.button
              key={d.title}
              onClick={() => navigate(d.route)}
              data-testid={`division-overview-${i}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: i * 0.08 }}
              className={`group relative text-left p-10 md:p-14 cgp-card-hover overflow-hidden ${
                d.theme === "dark"
                  ? "bg-[#0A1F44] text-white"
                  : "bg-white border border-[#0A1F44]/10 text-[#0A1F44]"
              }`}
            >
              {/* Decorative big number */}
              <span
                className={`absolute top-6 right-8 font-serif text-[8rem] leading-none font-light pointer-events-none select-none ${
                  d.theme === "dark"
                    ? "text-white/5"
                    : "text-[#0A1F44]/[0.04]"
                }`}
              >
                {String(i + 1).padStart(2, "0")}
              </span>

              <div
                className={`flex items-center gap-3 mb-10 text-xs uppercase tracking-[0.28em] ${
                  d.theme === "dark" ? "text-[#C8A24A]" : "text-[#0A1F44]/55"
                }`}
              >
                <d.icon
                  size={20}
                  strokeWidth={1.5}
                  className={d.theme === "dark" ? "text-[#C8A24A]" : "text-[#0A1F44]"}
                />
                <span>{d.tag}</span>
              </div>

              <h3
                className={`font-serif font-light text-4xl md:text-5xl tracking-tight leading-tight ${
                  d.theme === "dark" ? "text-white" : "text-[#0A1F44]"
                }`}
              >
                {d.title}
              </h3>

              <p
                className={`font-serif italic mt-3 text-lg md:text-xl ${
                  d.theme === "dark" ? "text-white/70" : "text-[#0A1F44]/65"
                }`}
              >
                {d.subtitle}
              </p>

              <span className="cgp-gold-rule mt-6" />

              <p
                className={`mt-6 max-w-md text-base leading-relaxed ${
                  d.theme === "dark" ? "text-white/75" : "text-[#0B1A36]/70"
                }`}
              >
                {d.desc}
              </p>

              <ul className="mt-7 space-y-2.5">
                {d.bullets.map((b) => (
                  <li
                    key={b}
                    className={`flex items-start gap-3 text-sm ${
                      d.theme === "dark" ? "text-white/80" : "text-[#0B1A36]/75"
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C8A24A] mt-2 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div
                className={`mt-12 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] font-semibold transition-all ${
                  d.theme === "dark"
                    ? "text-[#C8A24A] group-hover:text-[#D9B96B]"
                    : "text-[#0A1F44] group-hover:text-[#C8A24A]"
                }`}
              >
                {d.cta}
                <ArrowUpRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}
